/**
 * LA HISTORIA DE UN FOCO, en orden: qué se vio, qué se hizo y cómo fue.
 *
 * Es la respuesta a lo que se pidió: «que se vean en ese foco los muestreos que
 * había, los avances, y si hubo tratamiento». Pura —sin red y sin reloj— para
 * poder probarla; los datos los trae `useFocusTimeline`.
 *
 * TRES FUENTES, UNA LISTA:
 *  · los partes del foco: muestreos y revisiones limpias, cada uno con SU estado
 *    (así un muestreo ya tratado nunca queda tapado por otro sin tratar);
 *  · las tareas que cuelgan de él: tratamientos y seguimientos pedidos;
 *  · las pulverizaciones de SU plaga en su sector que nadie enlazó: no cambian
 *    el estado de nada, pero pasaron, y se enseñan como lo que son.
 */
import { deriveEvolution, type FocusEvolution, type LineState } from "./farmHealthModel";
import { samePest } from "./pestCanon";

export interface TimelineSession {
  id: string;
  date: string;
  created_at: string | null;
  kind: "sampling" | "clean_check";
  state: LineState;
  severity: string | null;
  verdict: FocusEvolution | null;
  line_number: number | null;
  inspector_id: string | null;
  photo_url: string | null;
  linked_task_id: string | null;
  followup_task_id: string | null;
  resolved_at: string | null;
}

export interface TimelineTask {
  id: string;
  type: string | null;
  status: string | null;
  title: string | null;
  scheduled_date: string | null;
  created_at: string | null;
  completed_at: string | null;
  assigned_to: string | null;
  /** `planner_tasks.followup_draft`: la revisión pedida a medias, si la hay. */
  followup_draft?: unknown;
}

export interface TimelineSpray {
  id: string;
  date: string;
  task_id: string | null;
  products: string[];
  /** Claves canónicas de las plagas a las que iba dirigida. */
  pestKeys: string[];
}

export type TimelineItem =
  | { kind: "sampling"; id: string; date: string; session: TimelineSession; evolution: FocusEvolution | null; isLatest: boolean }
  | { kind: "clean_check"; id: string; date: string; session: TimelineSession }
  | { kind: "treatment_planned"; id: string; date: string; task: TimelineTask }
  | { kind: "treatment_done"; id: string; date: string; task: TimelineTask; products: string[] }
  | { kind: "treatment_cancelled"; id: string; date: string; task: TimelineTask }
  | { kind: "followup_requested"; id: string; date: string; task: TimelineTask; done: boolean; cancelled?: boolean }
  | { kind: "spray_nearby"; id: string; date: string; products: string[] }
  /** Un lote de resolución; `by`/`reason` cuando lo cerró alguien a mano (D14) y la decisión se conserva. */
  | { kind: "resolved"; id: string; date: string; count: number; by?: string | null; reason?: string | null }
  /** `pastSampling`: lo reabrió un muestreo de ANTES del cierre, porque quien confirmó lo eligió (D, 23-09). */
  | { kind: "reopened"; id: string; date: string; by: string | null; pastSampling?: boolean }
  /** Se borró un tratamiento o un seguimiento del foco (antes desaparecía sin rastro). */
  | { kind: "task_removed"; id: string; date: string; at: string; by: string | null; taskType: string | null; title: string | null; wasDone: boolean }
  /** Se le unió otro foco (el absorbido desaparece; su historia pasa a este). */
  | { kind: "merged"; id: string; date: string; at: string; by: string | null; absorbedNumber: number | null }
  /** «out»: parte de sus muestreos se fueron a un foco nuevo; «in»: este nació así. */
  | { kind: "split"; id: string; date: string; at: string; by: string | null; direction: "out" | "in" }
  /** La revisión pedida ya se envió pero nadie la ha confirmado todavía (D2). */
  | { kind: "review_sent"; id: string; date: string; sessionId: string; inspector_id: string | null; verdict: string | null }
  /** El trabajador la tiene a medias (hay borrador en el servidor). */
  | { kind: "review_draft"; id: string; date: string; task: TimelineTask; missing: "verdict" | "media" | "finish" };

/** Una acción manual sobre el foco entero, tal como la guarda la RPC en `focus_link_decisions`. */
export interface TimelineDecision {
  action: string;
  decided_by: string | null;
  decided_at: string;
  detail: {
    resolved_at?: string;
    reopened_at?: string;
    reason?: string;
    notes?: string;
    by_past_sampling?: boolean;
    task_id?: string;
    task_type?: string | null;
    task_status?: string | null;
    title?: string | null;
    absorbed_focus_number?: number | null;
    cancelled_task_ids?: string[];
    /** Cuántos muestreos cerró el cierre (desde la auditoría del 23-09; los de antes no lo traen). */
    affected?: number | null;
    /** `task_removed`: cuándo se había completado la tarea quitada. */
    completed_at?: string | null;
  } | null;
}

export interface PendingReview {
  id: string;
  date: string;
  inspector_id: string | null;
  verdict: string | null;
}

const day = (ts: string | null | undefined): string | null => (ts ? ts.slice(0, 10) : null);
/** Dos sellos son el mismo si están a menos de 2 s: la RPC guarda `now()` en la decisión y en el lote. */
const mismoSello = (a: string, b: string) => a === b || Math.abs(Date.parse(a) - Date.parse(b)) < 2000;

export function draftMissingOf(draft: unknown): "verdict" | "media" | "finish" {
  const d = (draft ?? {}) as { verdict?: string | null; photos?: unknown[]; audio?: string | null };
  if (!d.verdict) return "verdict";
  if (!(Array.isArray(d.photos) && d.photos.length > 0) && !d.audio) return "media";
  return "finish";
}

/* A igualdad de día, el orden en que pasan las cosas en un invernadero: se ve,
   se pide, se trata, se da por resuelto. */
const ORDER: Record<TimelineItem["kind"], number> = {
  sampling: 0,
  clean_check: 1,
  followup_requested: 2,
  treatment_planned: 3,
  treatment_cancelled: 4,
  spray_nearby: 5,
  treatment_done: 6,
  resolved: 7,
  reopened: 8,
  review_sent: 1,
  review_draft: 1,
  task_removed: 8,
  merged: 9,
  split: 9,
};

export function buildFocusTimeline(input: {
  sessions: readonly TimelineSession[];
  tasks: readonly TimelineTask[];
  sprays: readonly TimelineSpray[];
  /** Clave canónica de la plaga del foco, para saber qué pulverización iba de lo mismo. */
  pestKey: string | null;
  /** Acciones manuales (cerrar con motivo, reabrir): opcionales, no fatales. */
  decisions?: readonly TimelineDecision[];
  /** Revisiones enviadas y aún sin confirmar. */
  pendingReviews?: readonly PendingReview[];
}): TimelineItem[] {
  const items: TimelineItem[] = [];
  const byAge = [...input.sessions].sort((a, b) =>
    a.date !== b.date ? (a.date < b.date ? -1 : 1) : (a.created_at ?? "") !== (b.created_at ?? "") ? ((a.created_at ?? "") < (b.created_at ?? "") ? -1 : 1) : a.id < b.id ? -1 : 1,
  );
  const samplings = byAge.filter((s) => s.kind === "sampling");

  samplings.forEach((s, i) => {
    items.push({
      kind: "sampling",
      id: s.id,
      date: s.date,
      session: s,
      evolution: deriveEvolution(i > 0 ? samplings[i - 1] : null, s),
      isLatest: i === samplings.length - 1,
    });
  });
  for (const s of byAge) if (s.kind === "clean_check") items.push({ kind: "clean_check", id: s.id, date: s.date, session: s });

  const sprayByTask = new Map<string, TimelineSpray[]>();
  for (const sp of input.sprays) {
    if (!sp.task_id) continue;
    sprayByTask.set(sp.task_id, [...(sprayByTask.get(sp.task_id) ?? []), sp]);
  }
  const taskIds = new Set(input.tasks.map((t) => t.id));

  for (const t of input.tasks) {
    const created = day(t.created_at) ?? t.scheduled_date ?? "";
    if (t.type === "monitoring") {
      items.push({ kind: "followup_requested", id: `${t.id}:pedido`, date: created, task: t, done: t.status === "completed", cancelled: t.status === "cancelled" });
      continue;
    }
    if (t.status === "cancelled") {
      items.push({ kind: "treatment_cancelled", id: `${t.id}:anulado`, date: created, task: t });
      continue;
    }
    if (t.status === "completed") {
      const hechas = sprayByTask.get(t.id) ?? [];
      const products = [...new Set(hechas.flatMap((sp) => sp.products))];
      items.push({ kind: "treatment_done", id: `${t.id}:hecho`, date: day(t.completed_at) ?? hechas[0]?.date ?? created, task: t, products });
    } else {
      items.push({ kind: "treatment_planned", id: `${t.id}:previsto`, date: t.scheduled_date ?? created, task: t });
    }
  }

  // Lo que se pulverizó para ESTA plaga sin que nadie lo enlazara al foco.
  const first = byAge[0]?.date ?? null;
  for (const sp of input.sprays) {
    if (sp.task_id && taskIds.has(sp.task_id)) continue;
    if (first == null || sp.date < first) continue;
    if (!sp.pestKeys.some((k) => samePest(k, input.pestKey))) continue;
    items.push({ kind: "spray_nearby", id: `${sp.id}:cerca`, date: sp.date, products: sp.products });
  }

  // Cada LOTE de resolución es un hito: «resolver el último resuelve el foco entero».
  const lotes = new Map<string, number>();
  for (const s of samplings) {
    if (s.state !== "resolved" || !s.resolved_at) continue;
    lotes.set(s.resolved_at, (lotes.get(s.resolved_at) ?? 0) + 1);
  }
  const decisions = input.decisions ?? [];
  for (const [stamp, count] of lotes) {
    const d = decisions.find((x) => (x.action === "close" || x.action === "clean_check") && x.detail?.resolved_at && mismoSello(x.detail.resolved_at, stamp));
    items.push({
      kind: "resolved",
      id: `resuelto:${stamp}`,
      date: stamp.slice(0, 10),
      count,
      by: d?.decided_by ?? null,
      reason: d ? (d.action === "clean_check" ? "clean" : d.detail?.reason ?? null) : null,
    });
  }
  /* UN CIERRE QUE YA NO ES UN LOTE SIGUE SIENDO HISTORIA (auditoría 23-09 ciclo#3, F15/F26). Al
     reabrir, los partes del lote dejan de estar resueltos y el hito «Cerrado por X · motivo» se
     iba con ellos: quedaba un «Reabierto por X» suelto. Se pinta a partir de la decisión que lo
     guardó. Lo mismo el lote que cerró un TRATAMIENTO hecho que luego se quitó (su borrado
     reabre el lote): el hito sale de `task_removed`, solo si ningún lote de entonces sigue ahí
     (si lo cerró alguien a mano antes, ese cierre sigue pintado y el tratamiento no resolvió nada). */
  const sellos = [...lotes.keys()];
  const hayLote = (at: string) => sellos.some((stamp) => mismoSello(stamp, at));
  for (const d of decisions) {
    const sello = d.detail?.resolved_at;
    if ((d.action === "close" || d.action === "clean_check") && sello && !hayLote(sello) && d.detail?.affected !== 0) {
      items.push({
        kind: "resolved",
        id: `resuelto:${sello}`,
        date: sello.slice(0, 10),
        count: d.detail?.affected ?? 0,
        by: d.decided_by,
        reason: d.action === "clean_check" ? "clean" : d.detail?.reason ?? null,
      });
    }
    const hecha = d.detail?.completed_at;
    if (
      d.action === "task_removed" && d.detail?.task_type === "treatment" && d.detail?.task_status === "completed" && hecha &&
      !sellos.some((stamp) => Date.parse(stamp) <= Date.parse(hecha) + 2000)
    ) {
      items.push({ kind: "resolved", id: `resuelto:${hecha}`, date: hecha.slice(0, 10), count: 0, by: null, reason: null });
    }
  }
  for (const d of decisions) {
    const date = d.decided_at.slice(0, 10);
    if (d.action === "reopen") {
      items.push({ kind: "reopened", id: `reabierto:${d.decided_at}`, date, by: d.decided_by, pastSampling: d.detail?.by_past_sampling === true });
    } else if (d.action === "task_removed") {
      items.push({
        kind: "task_removed",
        id: `quitada:${d.decided_at}:${d.detail?.task_id ?? ""}`,
        date,
        at: d.decided_at,
        by: d.decided_by,
        taskType: d.detail?.task_type ?? null,
        title: d.detail?.title ?? null,
        wasDone: d.detail?.task_status === "completed",
      });
    } else if (d.action === "merge") {
      items.push({ kind: "merged", id: `unido:${d.decided_at}`, date, at: d.decided_at, by: d.decided_by, absorbedNumber: d.detail?.absorbed_focus_number ?? null });
    } else if (d.action === "split_out" || d.action === "split") {
      items.push({ kind: "split", id: `separado:${d.action}:${d.decided_at}`, date, at: d.decided_at, by: d.decided_by, direction: d.action === "split_out" ? "out" : "in" });
    }
  }
  for (const p of input.pendingReviews ?? []) {
    items.push({ kind: "review_sent", id: `${p.id}:enviada`, date: p.date, sessionId: p.id, inspector_id: p.inspector_id, verdict: p.verdict });
  }
  for (const t of input.tasks) {
    if (t.followup_draft && t.status !== "completed" && t.status !== "cancelled") {
      items.push({ kind: "review_draft", id: `${t.id}:a-medias`, date: day(t.scheduled_date) ?? day(t.created_at) ?? "", task: t, missing: draftMissingOf(t.followup_draft) });
    }
  }

  /* A igualdad de día y de tipo manda LA ANTIGÜEDAD, no el id: dos muestreos del
     mismo día son lo normal (dos trabajadores, o el mismo por la mañana y por la
     tarde), y con un uuid de desempate el «más reciente» salía al azar — y con él
     la marca de avance de la cabecera, que se lee del último. */
  const edad = new Map(byAge.map((s, i) => [s.id, i]));
  const turno = (it: TimelineItem) => edad.get(it.id) ?? Number.MAX_SAFE_INTEGER;
  return items.sort((a, b) =>
    a.date !== b.date ? (a.date < b.date ? -1 : 1) : ORDER[a.kind] - ORDER[b.kind] || turno(a) - turno(b) || (a.id < b.id ? -1 : a.id > b.id ? 1 : 0),
  );
}

/** La HORA de un hito, cuando se sabe (ISO con zona); `null` cuando solo se sabe el día. */
export function whenOf(item: TimelineItem): string | null {
  switch (item.kind) {
    case "sampling":
    case "clean_check":
      return item.session.created_at ?? null;
    case "treatment_planned":
    case "treatment_cancelled":
    case "followup_requested":
      return item.task.created_at ?? null;
    case "treatment_done":
      return item.task.completed_at ?? item.task.created_at ?? null;
    case "resolved":
      return item.id.startsWith("resuelto:") ? item.id.slice("resuelto:".length) : null;
    case "reopened":
      return item.id.startsWith("reabierto:") ? item.id.slice("reabierto:".length) : null;
    case "task_removed":
    case "merged":
    case "split":
      return item.at;
    default:
      return null;
  }
}

/**
 * La historia COMO SE LEE: lo más reciente arriba (decisión de Pablo, 22-09-2026).
 * `buildFocusTimeline` sigue devolviendo de antiguo a nuevo (el mapa y la cabecera
 * cogen «el último» con `.pop()`); esto solo reordena para pintar. Dentro de un
 * mismo día manda la hora real cuando las dos partes la tienen; si no, se respeta
 * el orden de la historia, invertido.
 */
export function timelineForDisplay(items: readonly TimelineItem[]): TimelineItem[] {
  return items
    .map((item, index) => ({ item, index, at: whenOf(item) }))
    .sort((a, b) => {
      if (a.item.date !== b.item.date) return a.item.date < b.item.date ? 1 : -1;
      if (a.at && b.at && a.at !== b.at) return a.at < b.at ? 1 : -1;
      return b.index - a.index;
    })
    .map((x) => x.item);
}
